import {
    createContext,
    type PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import type { AnalyticsUserProfile } from '@/types/analytics-user-profile';

/* eslint-disable react-refresh/only-export-components */

type AnalyticsUserProfileContextValue = {
    profile: AnalyticsUserProfile | null;
    isLoading: boolean;
    setProfile: (profile: AnalyticsUserProfile | null) => void;
    beginAnalyticsProfileRefresh: () => void;
};

const defaultAnalyticsUserProfileContextValue: AnalyticsUserProfileContextValue = {
    profile: null,
    isLoading: false,
    setProfile: () => undefined,
    beginAnalyticsProfileRefresh: () => undefined,
};

const AnalyticsUserProfileContext = createContext<AnalyticsUserProfileContextValue>(
    defaultAnalyticsUserProfileContextValue
);

type AnalyticsUserProfileProviderInitialProfile = Promise<AnalyticsUserProfile | null> | AnalyticsUserProfile | null;

const AnalyticsUserProfileProvider = ({
    children,
    initialProfile,
}: PropsWithChildren<{ initialProfile?: AnalyticsUserProfileProviderInitialProfile }>) => {
    const localUpdateRef = useRef(false);
    const [profile, setProfileState] = useState<AnalyticsUserProfile | null>(() =>
        initialProfile instanceof Promise || initialProfile === undefined ? null : initialProfile
    );
    const [isLoading, setIsLoading] = useState(() => initialProfile instanceof Promise);

    useEffect(() => {
        localUpdateRef.current = false;

        if (initialProfile === undefined) {
            setProfileState(null);
            setIsLoading(false);
            return;
        }

        if (!(initialProfile instanceof Promise)) {
            setProfileState(initialProfile);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        let active = true;
        void initialProfile
            .then((nextProfile) => {
                if (active && !localUpdateRef.current) {
                    setProfileState(nextProfile);
                }
            })
            .finally(() => {
                if (active) {
                    setIsLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [initialProfile]);

    const setProfile = useCallback((nextProfile: AnalyticsUserProfile | null) => {
        localUpdateRef.current = true;
        setProfileState(nextProfile);
    }, []);

    const beginAnalyticsProfileRefresh = useCallback(() => {
        setIsLoading(true);
    }, []);

    const value = useMemo(
        () => ({
            profile,
            isLoading,
            setProfile,
            beginAnalyticsProfileRefresh,
        }),
        [profile, isLoading, setProfile, beginAnalyticsProfileRefresh]
    );

    return <AnalyticsUserProfileContext.Provider value={value}>{children}</AnalyticsUserProfileContext.Provider>;
};

export const useAnalyticsUserProfileContext = (): AnalyticsUserProfileContextValue => {
    return useContext(AnalyticsUserProfileContext);
};

export const useAnalyticsUserProfile = (): AnalyticsUserProfile | null => {
    return useAnalyticsUserProfileContext().profile;
};

export const useAnalyticsUserProfileLoading = (): boolean => {
    return useAnalyticsUserProfileContext().isLoading;
};

export default AnalyticsUserProfileProvider;
